import { Alert } from '@odekoteam/doppio';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Heading, Paragraph, Text } from 'theme-ui';

import {
  CategoryRow,
  formatBannerCoverage,
  toCategoryRows,
} from '@/components/Category/categoryRows';
import { MarketsCoverageCell } from '@/components/Category/MarketsCoverageCell';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import ErrorBanner from '@/components/ErrorBanner';
import {
  useGetCategoryTreeQuery,
  useGetWarehousesQuery,
} from '@/graphql/types/generated';
import { FeatureFlag, useFeatureFlipper } from '@/hooks/featureflipper';
import { useAppDispatch } from '@/hooks/store';
import TwoColLayout from '@/layouts/two-col';
import { ErrorActions } from '@/state/errors';

export default function CategoriesIndex() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isCategoryBannersEnabled = useFeatureFlipper(
    FeatureFlag.CategoryBanners,
  );
  // Paused until the flag is on. Hooks run before the guard below can redirect,
  // so without this a flag-off visitor typing the URL still fires both queries —
  // and the coverage fields are not on every environment's schema yet.
  const isOff = !isCategoryBannersEnabled;
  const [
    {
      data: warehousesData,
      fetching: fetchingWarehouses,
      error: warehousesError,
    },
  ] = useGetWarehousesQuery({ pause: isOff });
  const [{ data, fetching, error: categoriesError }] = useGetCategoryTreeQuery({
    pause: isOff,
    requestPolicy: 'cache-and-network',
  });

  // With the flag on in an environment whose schema lacks the coverage fields,
  // the query fails and an empty table would read as "no categories".
  const queryError = categoriesError ?? warehousesError;
  useEffect(() => {
    dispatch(ErrorActions.resetErrors());
    if (queryError) {
      dispatch(ErrorActions.setGraphqlErrors(queryError));
    }
  }, [dispatch, queryError]);

  if (isCategoryBannersEnabled === undefined) {
    return null;
  }
  if (!isCategoryBannersEnabled) {
    return <Navigate to="/" replace />;
  }

  const allWarehouses = warehousesData?.warehouses ?? [];

  const columns: DataTableColumn<CategoryRow>[] = [
    {
      header: 'Category',
      id: 'name',
      accessorFn: (row) => (
        <Text sx={{ paddingLeft: row.depth === 0 ? 0 : 4 }}>{row.name}</Text>
      ),
    },
    {
      header: 'Markets',
      id: 'markets',
      accessorFn: (row) => (
        <MarketsCoverageCell
          covered={row.activeWarehouses}
          allWarehouses={allWarehouses}
          tooltipId={`markets-${row.id}`}
        />
      ),
    },
    {
      header: 'Banner',
      id: 'banner',
      accessorFn: (row) => formatBannerCoverage(row, allWarehouses),
    },
  ];

  return (
    <TwoColLayout>
      <Box as="header">
        <Box sx={{ mb: 3, mt: 1 }}>
          <Alert variant="info">
            This tool should only be used by Marketing.
          </Alert>
        </Box>
        <Heading variant="headingMd">Categories</Heading>
        <Paragraph
          variant="paragraphLg"
          sx={{ color: 'neutralDark', marginTop: '2px' }}
        >
          Manage the banner shown above each category&rsquo;s products
        </Paragraph>
      </Box>
      <ErrorBanner />
      {(fetching || fetchingWarehouses) && <LoadingBox />}
      {!fetching && !fetchingWarehouses && !queryError && (
        <DataTable<CategoryRow>
          columns={columns}
          data={toCategoryRows(data?.categories ?? [])}
          onRowClick={(row) => navigate(`/categories/${row.id}`)}
        />
      )}
    </TwoColLayout>
  );
}
